/**
 * Utilitaires pour gérer les raccourcis clavier selon la plateforme
 * Détection robuste Mac / iOS / Windows / Android / Linux
 */

/**
 * Retourne la plateforme réelle de l'utilisateur
 * @returns {'mac' | 'ios' | 'windows' | 'android' | 'linux' | 'unknown'}
 */
export const getPlatform = () => {
    // 1API moderne Chromium (Chrome / Edge récents)
    if (navigator.userAgentData?.platform) {
        const platform = navigator.userAgentData.platform.toLowerCase();

        if (platform.includes('mac')) return 'mac';
        if (platform.includes('win')) return 'windows';
        if (platform.includes('android')) return 'android';
        if (platform.includes('linux')) return 'linux';
    }

    const ua = navigator.userAgent;

    // iPhone / iPod
    if (/iPhone|iPod/i.test(ua)) {
        return 'ios';
    }

    // iPadOS 13+ (userAgent contient "Macintosh")
    if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
        return 'ios';
    }

    // Mac
    if (/Mac/i.test(ua)) {
        return 'mac';
    }

    // Windows
    if (/Windows/i.test(ua)) {
        return 'windows';
    }

    // Android
    if (/Android/i.test(ua)) {
        return 'android';
    }

    // Linux
    if (/Linux/i.test(ua)) {
        return 'linux';
    }

    return 'unknown';
};

/**
 * Indique si l'appareil utilise la touche ⌘ (Apple)
 * @returns {boolean}
 */
export const isAppleDevice = () => {
    const platform = getPlatform();
    return platform === 'mac' || platform === 'ios';
};

/**
 * Retourne le symbole de la touche de modification
 * @returns {string} - "⌘" pour Apple, "Ctrl" sinon
 */
export const getModifierKey = () => {
    return isAppleDevice() ? '⌘' : 'Ctrl';
};

/**
 * Retourne le raccourci de recherche
 * @returns {string}
 */
export const getSearchShortcut = () => {
    return `${getModifierKey()}+K`;
};

/**
 * Retourne le raccourci d'ouverture du panneau raccourcis
 * @returns {string}
 */
export const getShortcutsModalShortcut = () => {
    return `${getModifierKey()}+/`;
};

/**
 * Retourne le raccourci de création d'un nouveau message
 * @returns {string}
 */
export const getNewMessageShortcut = () => {
    return `${getModifierKey()}+N`;
};

/**
 * Formate dynamiquement un raccourci clavier
 * @param {string} key - Exemple: "K", "Enter"
 * @param {Object} options
 * @param {boolean} options.withModifier - Ajoute Cmd/Ctrl
 * @param {boolean} options.withShift - Ajoute Shift
 * @param {boolean} options.withAlt - Ajoute Alt
 * @returns {string}
 */
export const formatShortcut = (
    key,
    { withModifier = false, withShift = false, withAlt = false } = {}
) => {
    const parts = [];

    if (withModifier) {
        parts.push(getModifierKey());
    }

    if (withShift) {
        parts.push('Shift');
    }

    if (withAlt) {
        parts.push('Alt');
    }

    parts.push(key);

    return parts.join('+');
};